import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { contactsApi } from '../../api/contacts'
import { dealsApi } from '../../api/deals'
import { methodologyApi } from '../../api/methodology'
import { RefusedError } from '../../api/client'
import { useJobLive } from '../../hooks/useJobLive'
import { useSpoke } from '../../hooks/useSpoke'
import { formatCompact, formatDate, formatMoney } from '../../lib/format'
import type { ContactView } from '../../types/contacts'
import type { DealStage, Engagement, Signal, OpportunityEvidence } from '../../types/deals'
import type { LeadPageView, LeadResearch } from '../../types/leads'
import type { Tactic } from '../../types/methodology'
import { Correct, CorrectionHistory } from '../../components/Correct'
import { PriorWorkCard } from '../../components/PriorWork'
import { CalloutCard } from './Callouts'
import { ResearchCorrected } from '../../components/Research/CorrectedNote'
import { LeadMap } from './LeadMap'
import { OpportunityCorrespondence, OpportunityDocuments, OpportunityPeople } from './OpportunityOnLead'
import { LADDER, PERSONA_GLYPH, PERSONA_ORDER, PERSONA_WORDS, STAGE_WORDS } from './stages'
import './leads.css'

// One lead, opened: the callouts the state raises, the pursuit at a glance, the influence map of the account
// (who signs, who champions, who covers them), the thread evidence and the plays run, what the firm already
// knows about the account, and the research note the controller writes from what is on file. Everything on
// this page traces to a message, a signal, a meeting or the company record; nothing is looked up elsewhere.

const OPEN: DealStage[] = ['lead', 'opportunity', 'proposal', 'negotiation']
const PIPELINE: DealStage[] = ['opportunity', 'proposal', 'negotiation', 'won']

export function LeadPage({ tab }: { tab: 'leads' | 'opportunities' }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const { engagementId = '' } = useParams()
  const q = useQuery({ queryKey: ['lead', spoke, engagementId], queryFn: () => dealsApi.lead(spoke, engagementId), staleTime: 10_000 })
  const [jobId, setJobId] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [persona, setPersona] = useState<string | null>(null)
  const [playTactic, setPlayTactic] = useState('')
  const research = useMutation({ mutationFn: () => dealsApi.research(spoke, engagementId), onSuccess: (r) => setJobId(r.job.id) })
  const liveId = jobId ?? q.data?.activeResearchJobId ?? undefined
  const { state: live } = useJobLive(spoke, liveId)
  const landed = live ? live.job.status === 'Completed' || live.job.status === 'Failed' || live.job.status === 'Cancelled' : false
  useEffect(() => {
    if (!landed) return
    void qc.invalidateQueries({ queryKey: ['lead', spoke, engagementId] })
  }, [landed, qc, spoke, engagementId])
  // stable props for the map, so a selection or a filter never rebuilds it (a dragged layout survives)
  const names = q.data?.ownerNames
  const coverageNamed = useMemo(() => (q.data?.account.coverage ?? []).map((c) => ({ ...c, name: names?.[c.ownerId] ?? c.ownerId })), [q.data, names])
  const onOpenPerson = useCallback((id: string) => setSelected((s) => (s === id ? null : id)), [])

  if (q.isPending) return <p className="muted centered">Loading…</p>
  if (q.isError || !q.data) {
    return (
      <section className="page lead">
        <p className="kicker"><Link to={`/${tab}`}>{tab === 'leads' ? 'Leads' : 'Opportunities'}</Link></p>
        <h1>Not on your pipeline</h1>
        <div className="refusal"><strong>No such engagement</strong><p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error?.message ?? 'Nothing here.'}</p></div>
      </section>
    )
  }

  const page = q.data
  const e = page.deal.engagement
  const company = page.deal.company
  const contacts = page.account.contacts
  const others = page.account.engagements.filter((x) => x.engagementId !== e.engagementId)
  const owner = (id: string) => page.ownerNames[id] ?? id
  const researching = !!live && !landed
  // A note the person has said is wrong is not shown. Its key as a correction entity is the pursuit the
  // page was opened from; the service resolves that to the account the note is actually filed under.
  const note = page.research
  const noteId = `company:${e.engagementId}`
  const corrected = !!note && note.status !== 'current'

  return (
    <section className="page lead">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to={`/${tab}`}>{tab === 'leads' ? 'Leads' : 'Opportunities'}</Link>{company.domain ? ` · ${company.domain}` : ''} · {owner(e.ownerId)}</p>
          <h1>{company.name}</h1>
          <p className="lede"><strong>{e.subject}.</strong> {e.summary}</p>
        </div>
        <div className="actions">
          <button className="button primary" onClick={() => research.mutate()} disabled={research.isPending || researching || !page.modelConfigured}
            title={page.modelConfigured ? 'The controller writes a research note from what is on file: the mail, the pipeline, the people, the signals, the calendar. No web.' : 'Needs the CLI controller or an API model.'}>
            {researching ? 'Researching…' : research.isPending ? 'Starting…' : page.research ? 'Research again' : 'Research this company'}
          </button>
          <Link className="button" to={`/accounts/${encodeURIComponent(company.companyId)}`}>Account plan</Link>
          <Link className="button" to={`/workbench/${encodeURIComponent(company.companyId)}?opportunity=${encodeURIComponent(e.engagementId)}`}>Work this opportunity</Link>
          <Correct kind={tab === 'leads' ? 'lead' : 'opportunity'} entityId={e.engagementId} entityLabel={`${company.name} · ${e.subject}`}
            currentValue={e.stage} label="Not right?" />
        </div>
      </header>
      {research.isError && <p className="error">{research.error instanceof RefusedError ? research.error.refusal.message : research.error.message}</p>}

      {page.callouts.length > 0 && (
        <ul className="lead-callouts" aria-label="What to notice">
          {page.callouts.map((c, i) => <CalloutCard key={`${c.kind}-${i}`} c={c} modelConfigured={page.modelConfigured} onPerson={(id) => setSelected(id)} onPlay={(tacticId) => { setPlayTactic(tacticId); document.getElementById('plays')?.scrollIntoView({ behavior: 'smooth', block: 'center' }) }} />)}
        </ul>
      )}

      <Glance e={e} contacts={contacts} others={others} coverage={coverageNamed} evidence={page.deal.evidence} />

      <div className="lead-body">
        <div className="lead-main">
          <div className="card lead-map-card">
            <div className="card-head">
              <div>
                <h2>Influence map</h2>
                <p className="muted">{contacts.length === 0 ? 'Nobody at the account is on file yet; the people you write to become the map.' : `${contacts.length} ${contacts.length === 1 ? 'person' : 'people'} at ${company.name}, sized by how strong your tie is, placed by the part they play. Drag to rearrange; click to open.`}</p>
              </div>
            </div>
            <PersonaChips contacts={contacts} current={persona} onPick={setPersona} />
            {contacts.length > 0 && (
              <div className={`lead-map-row ${selected ? 'with-panel' : ''}`}>
                <LeadMap contacts={contacts} companyName={company.name} firmName={page.firmName} coverage={coverageNamed} selected={selected} personaFilter={persona} onOpen={onOpenPerson} />
                {selected && <PersonPanel spoke={spoke} personId={selected} contact={contacts.find((c) => c.person.personId === selected) ?? null} onClose={() => setSelected(null)} />}
              </div>
            )}
            <p className="lead-map-legend">
              <span><i className="role customer" />customer</span><span><i className="role prospect" />prospect</span><span><i className="role partner" />partner</span><span><i className="role vendor" />vendor</span><span><i className="role colleague" />colleague</span>
              <span className="muted">size = strength · dashed = may have moved · faded = gone quiet</span>
            </p>
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h2>The pursuit</h2>
                <p className="muted">The thread that put {company.name} here, and every signal the model read from it. Each line traces to a message you sent.</p>
              </div>
            </div>
            {page.opportunity && <OpportunityPeople spoke={spoke} opportunity={page.opportunity} />}
            <Evidence e={e} signals={page.deal.signals} />
            {page.opportunity && <OpportunityCorrespondence opportunity={page.opportunity} />}
            {page.opportunity && <OpportunityDocuments spoke={spoke} opportunity={page.opportunity} />}
            <CorrectionHistory entityId={e.engagementId} />
            <div id="plays">
              <PlaysPanel engagementId={e.engagementId} methodologyId={page.methodologyId} tactics={page.tactics} preset={playTactic} onPresetUsed={() => setPlayTactic('')} />
            </div>
          </div>
        </div>

        <aside className="lead-side">
          <div className="card">
            <div className="card-head">
              <div>
                <h2>Account background</h2>
                <p className="muted">What the firm's own data already says about {company.name}.</p>
              </div>
            </div>
            <Background page={page} current={e} others={others} owner={owner} />
          </div>

          <div className={`card research-card ${note ? 'on-file' : ''}`}>
            <div className="card-head">
              <div>
                <h2>Market details</h2>
                <p className="muted">A research note written from what is on file: the company record, your mail to them, the people, the signals, the calendar. The controller has no web here; what is not on file is said to be.</p>
              </div>
              {/* The note is an assertion like any other, so it can be told it is wrong — the wrong company,
                  a wrong fact, out of date, or no use — and the next one is written under that reason. */}
              {note && <Correct kind="research" entityId={noteId} entityLabel={`Market details — ${company.name}`} label="Not right?" />}
            </div>
            {researching && live && (
              <div className="research-live">
                <p className="kicker">Researching · <Link to={`/jobs/${encodeURIComponent(live.job.id)}`}>open the console</Link></p>
                <ol className="research-steps">
                  {live.job.steps.map((s) => <li key={s.stepId} className={s.status.toLowerCase()}><span>{s.label}</span>{s.message && <em>{s.message}</em>}</li>)}
                </ol>
              </div>
            )}
            {/* A research job that FAILED must say so here, where the person pressed the button. Before this,
                the page fell straight back to the button and the failure lived only on the job console —
                which read, to the person, as "the button doesn't do anything" (owner, 2026-09-13). */}
            {live && live.job.status === 'Failed' && (
              <div className="refusal" role="alert">
                <strong>The research note could not be written.</strong>
                <p>{live.job.error ?? live.job.steps.find((s) => s.status === 'Failed')?.message ?? 'The job failed.'}</p>
                <p className="muted small"><Link to={`/jobs/${encodeURIComponent(live.job.id)}`}>Open the console</Link> for the full record, then try again.</p>
              </div>
            )}
            {!researching && !note && (
              <div className="research-empty">
                <p>No note yet.</p>
                <button className="button primary" onClick={() => research.mutate()} disabled={research.isPending || !page.modelConfigured}>Research this company</button>
                {!page.modelConfigured && <p className="muted small">Needs the CLI controller or an API model.</p>}
              </div>
            )}
            {note && corrected && (
              <ResearchCorrected entityId={noteId} correctionId={note.correctionId} status={note.status}
                researching={researching} canResearch={page.modelConfigured} onResearch={() => research.mutate()} />
            )}
            {note && !corrected && <ResearchNote r={note} stale={researching} />}
          </div>

          <PriorWorkCard spoke={spoke} companyId={company.companyId} companyName={company.name} />
        </aside>
      </div>
    </section>
  )
}

/* ── pieces ── */

function Glance({ e, contacts, others, coverage, evidence }: { e: Engagement; contacts: ContactView[]; others: Engagement[]; coverage: { ownerId: string; name: string; contacts: number; engagements: number; strength: number }[]; evidence?: OpportunityEvidence | null }) {
  const strongest = contacts.reduce((m, c) => Math.max(m, c.person.relationshipStrength ?? 0), 0)
  const signers = contacts.filter((c) => c.person.persona === 'decision-maker').length
  const champions = contacts.filter((c) => c.person.persona === 'champion').length
  const days = e.lastActivityOn ? Math.max(0, Math.round((Date.now() - new Date(e.lastActivityOn).getTime()) / 86_400_000)) : null
  const pipeline = others.filter((x) => PIPELINE.includes(x.stage)).reduce((n, x) => n + (x.amount ?? 0), 0) + (PIPELINE.includes(e.stage) ? e.amount ?? 0 : 0)
  const open = others.filter((x) => OPEN.includes(x.stage)).length
  const onLadder = LADDER.includes(e.stage)
  return (
    <section className="glance lead-glance">
      <div className="stage-ladder">
        <p className="kicker">Where it stands</p>
        <ol>
          {LADDER.map((s, i) => {
            const idx = LADDER.indexOf(e.stage)
            const state = !onLadder ? 'off' : i < idx ? 'done' : i === idx ? 'here' : 'ahead'
            return <li key={s} className={state}><i />{STAGE_WORDS[s]}</li>
          })}
        </ol>
        {!onLadder && <p className={`ladder-side ${e.stage}`}>{STAGE_WORDS[e.stage]}</p>}
        {evidence
          ? <p className="muted small" title={`The scan's own read was ${Math.round(e.confidence * 100)}%; the evidence is what counts.`}>confidence {Math.round(evidence.score * 100)}% from the facts on the row: {evidence.basis.join('; ')} · read {formatDate(e.extractedAt)}</p>
          : <p className="muted small">the scan's own read {Math.round(e.confidence * 100)}% · read {formatDate(e.extractedAt)}</p>}
      </div>

      <div className="glance-words">
        <p className="kicker">The pursuit, in words</p>
        <p className="glance-summary">{e.summary}</p>
        <dl className="glance-conventions">
          <dt>Amount</dt><dd>{e.amount != null ? <span className="chip"><em>{formatMoney(e.amount)}</em> {e.amountCurrency} · {e.amountBasis}</span> : <span className="muted">none named</span>}</dd>
          <dt>Paper</dt>
          <dd>
            <span className={`chip ${e.sowSent ? '' : 'off'}`}>{e.sowSent ? `SOW sent${e.sowSentOn ? ' ' + formatDate(e.sowSentOn) : ''}${e.sowName ? ' · ' + e.sowName : ''}` : 'no SOW'}</span>
            <span className={`chip ${e.proposalSent ? '' : 'off'}`}>{e.proposalSent ? `proposal sent${e.proposalSentOn ? ' ' + formatDate(e.proposalSentOn) : ''}` : 'no proposal'}</span>
          </dd>
          <dt>Next</dt>
          <dd>
            {e.nextStep ? <span className={`chip ${e.nextStepOwner === 'me' ? 'owe' : ''}`}><em>{e.nextStep}</em> {e.nextStepOwner === 'me' ? 'you owe it' : e.nextStepOwner === 'them' ? 'they owe it' : ''}{e.nextStepBy ? ` · by ${formatDate(e.nextStepBy)}` : ''}</span> : <span className="muted">no open step</span>}
            {e.nextStep && <Correct kind="next-step" entityId={e.engagementId} entityLabel={e.nextStep} currentValue={e.nextStepOwner === 'me' ? 'mine' : e.nextStepOwner === 'them' ? 'theirs' : null} compact />}
          </dd>
          {e.serviceLine && <><dt>Line</dt><dd><span className="chip">{e.serviceLine}</span></dd></>}
        </dl>
      </div>

      <div className="glance-stats">
        <div className="glance-stat"><strong>{contacts.length}</strong><span>people on file</span></div>
        <div className="glance-stat"><strong>{contacts.length ? `${Math.round(strongest * 100)}%` : '—'}</strong><span>strongest tie</span></div>
        <div className="glance-stat"><strong>{signers}<small> · {champions}</small></strong><span>decision makers · champions</span></div>
        <div className="glance-stat"><strong>{days ?? '—'}</strong><span>days since last activity</span></div>
        <div className="glance-stat"><strong>{open}</strong><span>other open pursuits here</span></div>
        <div className="glance-stat" title={pipeline > 0 ? formatMoney(pipeline) : ''}><strong>{pipeline > 0 ? (pipeline >= 1_000_000 ? '$' + formatCompact(pipeline) : formatMoney(pipeline)) : '—'}</strong><span>pipeline at the account</span></div>
        <div className="glance-busiest">
          <p className="kicker">Who covers this account</p>
          {coverage.length === 0 ? <p className="muted small">Nobody yet.</p> : (
            <ul>{coverage.slice(0, 4).map((c) => <li key={c.ownerId}><span>{c.name}</span><span className="muted">{c.contacts} {c.contacts === 1 ? 'contact' : 'contacts'} · {Math.round(c.strength * 100)}%</span></li>)}</ul>
          )}
        </div>
      </div>
    </section>
  )
}

function PersonaChips({ contacts, current, onPick }: { contacts: ContactView[]; current: string | null; onPick: (p: string | null) => void }) {
  const counts = new Map<string, number>()
  for (const c of contacts) { const k = c.person.persona ?? 'other'; counts.set(k, (counts.get(k) ?? 0) + 1) }
  if (contacts.length === 0) return null
  return (
    <div className="persona-chips">
      <button className={`chip-filter ${current === null ? 'on' : ''}`} onClick={() => onPick(null)}><strong>{contacts.length}</strong> <span>everyone</span></button>
      {PERSONA_ORDER.filter((p) => counts.get(p)).map((p) => (
        <button key={p} className={`chip-filter ${current === p ? 'on' : ''}`} onClick={() => onPick(current === p ? null : p)}><strong>{counts.get(p)}</strong> <span>{PERSONA_GLYPH[p]} {PERSONA_WORDS[p]}{(counts.get(p) ?? 0) === 1 ? '' : 's'}</span></button>
      ))}
    </div>
  )
}

// The person opened from the map: their memory (PM-006) so the next touch never repeats first-contact language.
function PersonPanel({ spoke, personId, contact, onClose }: { spoke: string; personId: string; contact: ContactView | null; onClose: () => void }) {
  const q = useQuery({ queryKey: ['contact', spoke, personId], queryFn: () => contactsApi.memory(spoke, personId) })
  const p = contact?.person
  const m = q.data
  return (
    <aside className="person-panel">
      <div className="actions space-between">
        <div>
          <p className="kicker">{p?.persona ? PERSONA_WORDS[p.persona] ?? p.persona : 'contact'}{p?.role ? ` · ${p.role}` : ''}</p>
          <h3>{p?.name ?? m?.contact.person.name ?? 'Contact'}</h3>
        </div>
        <button className="button small" onClick={onClose}>Close</button>
      </div>
      {p && (
        <dl className="facts compact">
          {p.title && <><dt>Title</dt><dd>{p.title}</dd></>}
          {p.email && <><dt>Email</dt><dd>{p.email}</dd></>}
          <dt>Tie</dt><dd>{Math.round((p.relationshipStrength ?? 0) * 100)}% · {contact!.lastActivity ? `${formatDate(contact!.lastActivity)} (${contact!.dormancyDays}d ago)` : 'no touch on record'}</dd>
          {(p.affiliations?.length ?? 0) > 0 && <><dt>Affinity</dt><dd>{p.affiliations!.join(', ')}</dd></>}
        </dl>
      )}
      {p && (
        <p className="person-panel-correct">
          <Correct kind="person-company" entityId={personId} entityLabel={p.name} currentValue={p.companyId} compact />
          <Correct kind="contact-role" entityId={personId} entityLabel={p.name} currentValue={p.role} compact />
          <span className="muted small">Wrong company, or the wrong part to play? Say so.</span>
        </p>
      )}
      {q.isPending && <p className="muted small">Loading…</p>}
      {m && (
        <>
          {m.pursuits.length > 0 && <><h4>Pursuits they sit in</h4><ul className="plain">{m.pursuits.slice(0, 5).map((x) => <li key={x.engagementId}><strong>{x.subject}</strong><span className="muted small"> · {STAGE_WORDS[x.stage] ?? x.stage}{x.lastActivityOn ? ` · ${formatDate(x.lastActivityOn)}` : ''}</span></li>)}</ul></>}
          <h4>History</h4>
          {m.signals.length === 0 ? <p className="muted small">Nothing flagged.</p> : <ul className="plain signals">{m.signals.slice(0, 8).map((s) => <li key={s.signalId}><span className={`tag sig ${s.severity}`}>{s.type}</span> {s.headline}<span className="muted small"> · {formatDate(s.occurredOn)}</span></li>)}</ul>}
        </>
      )}
    </aside>
  )
}

function Evidence({ e, signals }: { e: Engagement; signals: Signal[] }) {
  const sorted = [...signals].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn))
  return (
    <div className="evidence">
      <p className="evidence-thread"><span className="kicker">Thread</span> <strong>{e.subject}</strong> <span className="muted small">last activity {e.lastActivityOn ? formatDate(e.lastActivityOn) : 'unknown'} · read by {e.extractedBy}</span></p>
      {sorted.length === 0 ? <p className="muted">No signals were read from this thread.</p> : (
        <ol className="evidence-list">
          {sorted.map((s) => (
            <li key={s.signalId} className={`sev-${s.severity}`}>
              <time>{formatDate(s.occurredOn)}</time>
              <div>
                <span className={`tag sig ${s.severity}`}>{s.type}</span> <strong>{s.headline}</strong>
                {(s.detail || s.amount != null) && <span className="muted small">{s.amount != null ? formatMoney(s.amount) : ''}{s.amount != null && s.detail ? ' · ' : ''}{s.detail ?? ''}</span>}
                <span className="muted small evidence-src">{s.messageId ? 'from a message you sent' : s.source.startsWith('thread:') ? 'from the thread' : s.source}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}

function Background({ page, current, others, owner }: { page: LeadPageView; current: Engagement; others: Engagement[]; owner: (id: string) => string }) {
  const prior = others.filter((x) => x.stage === 'won' || x.stage === 'delivery' || x.stage === 'lost')
  const open = others.filter((x) => OPEN.includes(x.stage) || x.stage === 'dormant')
  const watch = page.account.contacts.filter((c) => c.signals.some((s) => s.type === 'dormant' || s.type === 'job-change'))
  const news = page.account.signals.filter((s) => s.engagementId !== current.engagementId && s.source !== 'contact-watch').slice(0, 6)
  const route = (x: Engagement) => (x.stage === 'lead' || x.stage === 'dormant' ? `/leads/${encodeURIComponent(x.engagementId)}` : `/opportunities/${encodeURIComponent(x.engagementId)}`)
  const byRecency = (a: Engagement, b: Engagement) => (b.lastActivityOn ?? '').localeCompare(a.lastActivityOn ?? '')
  const won = prior.filter((x) => x.stage !== 'lost').length
  const fees = prior.reduce((n, x) => n + (x.amount ?? 0), 0)
  const more = (n: number, shown: number) => (n > shown ? <li className="muted small more"><Link to={`/accounts/${encodeURIComponent(page.deal.company.companyId)}`}>+{n - shown} more on the account plan</Link></li> : null)
  return (
    <div className="background">
      <h3>Prior engagements</h3>
      {prior.length === 0 ? <p className="muted small">None on file. {page.deal.company.name} has not bought from the firm on your mail's record.</p> : (
        <>
          <p className="background-sum">{won} won or delivering{prior.length - won > 0 ? `, ${prior.length - won} lost` : ''}{fees > 0 ? ` · ${formatMoney(fees)} named` : ''}</p>
          <ul className="plain">{[...prior].sort(byRecency).slice(0, 6).map((x) => <li key={x.engagementId}><Link to={route(x)}><strong>{x.subject}</strong></Link><span className="muted small"> · {STAGE_WORDS[x.stage]}{x.serviceLine ? ` · ${x.serviceLine}` : ''}{x.amount ? ` · ${formatMoney(x.amount)}` : ''}{x.lastActivityOn ? ` · ${formatDate(x.lastActivityOn)}` : ''} · {owner(x.ownerId)}</span></li>)}{more(prior.length, 6)}</ul>
        </>
      )}
      <h3>Other pursuits here</h3>
      {open.length === 0 ? <p className="muted small">This is the only live thread at the account.</p> : (
        <ul className="plain">{[...open].sort(byRecency).slice(0, 6).map((x) => <li key={x.engagementId}><Link to={route(x)}><strong>{x.subject}</strong></Link><span className="muted small"> · {STAGE_WORDS[x.stage]}{x.amount ? ` · ${formatMoney(x.amount)}` : ''}{x.lastActivityOn ? ` · ${formatDate(x.lastActivityOn)}` : ''} · {owner(x.ownerId)}</span></li>)}{more(open.length, 6)}</ul>
      )}
      <h3>Meetings</h3>
      {page.meetings.length === 0 ? <p className="muted small">None on your calendar with this account.</p> : (
        <ul className="plain">{page.meetings.slice(0, 6).map((m) => <li key={m.meetingId} className={m.isCancelled ? 'cancelled' : ''}><strong>{m.subject}</strong><span className="muted small"> · {formatDate(m.start)}{m.isCancelled ? ' · cancelled' : ''}</span>{m.transcriptId && <Link className="small" to="/transcripts"> · transcript</Link>}</li>)}</ul>
      )}
      <h3>People to watch</h3>
      {watch.length === 0 ? <p className="muted small">Nobody has gone quiet or moved.</p> : (
        <ul className="plain">{watch.map((c) => <li key={c.person.personId}><strong>{c.person.name}</strong><span className="muted small"> · {c.signals.filter((s) => s.type === 'dormant' || s.type === 'job-change').map((s) => s.headline).join(' · ')}</span></li>)}</ul>
      )}
      {news.length > 0 && (
        <>
          <h3>Account news</h3>
          <ul className="plain signals">{news.map((s) => <li key={s.signalId}><span className={`tag sig ${s.severity}`}>{s.type}</span> {s.headline}<span className="muted small"> · {formatDate(s.occurredOn)}</span></li>)}</ul>
        </>
      )}
    </div>
  )
}

function ResearchNote({ r, stale }: { r: LeadResearch; stale: boolean }) {
  return (
    <div className={`research ${stale ? 'stale' : ''}`}>
      <div className="research-facts">
        <span className={`chip ${r.sector ? '' : 'off'}`}><b>Sector</b> {r.sector ?? 'not on file'}</span>
        <span className={`chip ${r.sizeTier ? '' : 'off'}`}><b>Size</b> {r.sizeTier ?? 'not on file'}</span>
      </div>
      <p className="research-summary">{r.summary}</p>

      <h3>Likely needs, by service line</h3>
      {r.likelyNeeds.length === 0 ? <p className="muted small">None the file supports.</p> : (
        <ul className="plain needs">
          {r.likelyNeeds.map((n) => <li key={n.serviceLine + n.why}><strong>{n.serviceLine}</strong><span className={`tag basis ${n.basis === 'on file' ? 'on' : ''}`}>{n.basis}</span><span className="muted small">{n.why}</span></li>)}
        </ul>
      )}

      <h3>Competitors named in the mail</h3>
      {r.competitors.length === 0 ? <p className="muted small">None named.</p> : <ul className="chips">{r.competitors.map((c) => <li key={c} className="chip on">{c}</li>)}</ul>}

      <h3>Risks</h3>
      {r.risks.length === 0 ? <p className="muted small">None the file shows.</p> : <ul className="research-risks">{r.risks.map((x) => <li key={x}>{x}</li>)}</ul>}

      {r.notOnFile.length > 0 && (
        <div className="research-gaps">
          <strong>Not on file</strong>
          <ul>{r.notOnFile.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}
      <details className="research-grounding">
        <summary>Grounded on</summary>
        <ul>{r.groundedOn.map((g) => <li key={g}>{g}</li>)}</ul>
      </details>
      <p className="muted small research-stamp">Written {formatDate(r.researchedAt)} by {r.modelId}.{stale ? ' A newer note is being written.' : ''}</p>
    </div>
  )
}

// Plays run: follow a tactic from the methodology on this pursuit and record how it landed. The Playbook
// measures conversion from these, so the methodology is not just read, it is FOLLOWED and MEASURED.
const OUTCOME_WORDS: Record<string, string> = { booked: 'booked a meeting', advanced: 'advanced', 'no-response': 'no response', declined: 'declined', won: 'won' }

function PlaysPanel({ engagementId, methodologyId, tactics, preset, onPresetUsed }: { engagementId: string; methodologyId: string | null; tactics: Tactic[]; preset: string; onPresetUsed: () => void }) {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const apps = useQuery({ queryKey: ['applications', spoke, methodologyId, engagementId], queryFn: () => methodologyApi.applications(spoke, methodologyId!, engagementId), enabled: !!methodologyId })
  const [tacticId, setTacticId] = useState('')
  const [outcome, setOutcome] = useState('')
  useEffect(() => { if (preset) { setTacticId(preset); onPresetUsed() } }, [preset, onPresetUsed])
  const apply = useMutation({
    mutationFn: () => methodologyApi.apply(spoke, methodologyId!, tacticId, { engagementId, status: 'applied', outcome: outcome || undefined }),
    onSuccess: () => {
      setTacticId(''); setOutcome('')
      void qc.invalidateQueries({ queryKey: ['applications', spoke, methodologyId, engagementId] })
      void qc.invalidateQueries({ queryKey: ['methodology', spoke, methodologyId] })
      void qc.invalidateQueries({ queryKey: ['lead', spoke, engagementId] })
    },
  })
  if (!methodologyId) return null
  const name = (id: string) => tactics.find((t) => t.tacticId === id)?.name ?? id
  const run = apps.data ?? []
  return (
    <div className="plays">
      <h3>Plays run</h3>
      {run.length > 0 && (
        <ul className="plain plays-run">
          {run.map((a) => (
            <li key={a.applicationId}>
              <strong>{name(a.tacticId)}</strong>
              <span className="muted small">{a.outcome ? OUTCOME_WORDS[a.outcome] ?? a.outcome : a.status} · {a.appliedOn}</span>
              <Correct kind="play" entityId={a.applicationId} entityLabel={name(a.tacticId)} currentValue={`tactic:${a.tacticId}`} compact />
            </li>
          ))}
        </ul>
      )}
      <div className="play-apply">
        <select value={tacticId} onChange={(ev) => setTacticId(ev.target.value)}>
          <option value="">Run a play…</option>
          {tactics.map((t) => <option key={t.tacticId} value={t.tacticId}>{t.name}</option>)}
        </select>
        <select value={outcome} onChange={(ev) => setOutcome(ev.target.value)} disabled={!tacticId}>
          <option value="">how it landed…</option>
          {Object.entries(OUTCOME_WORDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button className="button small" disabled={!tacticId || apply.isPending} onClick={() => apply.mutate()}>{apply.isPending ? 'Saving…' : 'Record'}</button>
      </div>
      {tacticId && tactics.find((t) => t.tacticId === tacticId)?.howTo && <p className="play-how muted small">{tactics.find((t) => t.tacticId === tacticId)!.howTo}</p>}
    </div>
  )
}
